import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';

type Preset = 'all' | 'this_month' | 'last_month' | 'last_3_months' | 'this_year' | 'custom';

@Component({
  selector: 'app-date-range-picker-modal',
  templateUrl: './date-range-picker-modal.component.html',
  styleUrls: ['./date-range-picker-modal.component.scss'],
})
export class DateRangePickerModalComponent implements OnInit {

  @Input() from: string | null = null;
  @Input() to: string | null = null;
  @Input() max: string | null = null;
  @Input() title: string = 'Filtrar por fecha';

  fromDate: string = '';
  toDate: string = '';
  maxDate: string = '';
  selectedPreset: Preset = 'custom';

  constructor(private modalController: ModalController) {}

  ngOnInit() {
    this.fromDate = this.toIsoDate(this.from);
    this.toDate = this.toIsoDate(this.to);
    this.maxDate = this.max ? this.toIsoDate(this.max) : this.todayIso();
    this.selectedPreset = this.detectPreset();
  }

  private todayIso(): string {
    const d = new Date();
    return this.formatIso(d);
  }

  private formatIso(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private toIsoDate(value: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return this.formatIso(d);
  }

  private detectPreset(): Preset {
    if (!this.fromDate && !this.toDate) return 'all';
    return 'custom';
  }

  applyPreset(preset: Preset) {
    this.selectedPreset = preset;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (preset) {
      case 'all':
        this.fromDate = '';
        this.toDate = '';
        break;
      case 'this_month': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        this.fromDate = this.formatIso(start);
        this.toDate = this.formatIso(today);
        break;
      }
      case 'last_month': {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        this.fromDate = this.formatIso(start);
        this.toDate = this.formatIso(end);
        break;
      }
      case 'last_3_months': {
        const start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        this.fromDate = this.formatIso(start);
        this.toDate = this.formatIso(today);
        break;
      }
      case 'this_year': {
        const start = new Date(today.getFullYear(), 0, 1);
        this.fromDate = this.formatIso(start);
        this.toDate = this.formatIso(today);
        break;
      }
    }
  }

  onCustomDateChange() {
    this.selectedPreset = 'custom';
  }

  get rangeLabel(): string {
    if (!this.fromDate && !this.toDate) return 'Cualquier fecha';
    if (this.fromDate && !this.toDate) return `Desde el ${this.formatHuman(this.fromDate)}`;
    if (!this.fromDate && this.toDate) return `Hasta el ${this.formatHuman(this.toDate)}`;
    return `${this.formatHuman(this.fromDate)} – ${this.formatHuman(this.toDate)}`;
  }

  get isValid(): boolean {
    if (!this.fromDate && !this.toDate) return true;
    if (this.fromDate && this.toDate) {
      return new Date(this.fromDate) <= new Date(this.toDate);
    }
    return true;
  }

  private formatHuman(iso: string): string {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  clearAll() {
    this.fromDate = '';
    this.toDate = '';
    this.selectedPreset = 'all';
  }

  apply() {
    if (!this.isValid) return;
    this.modalController.dismiss({
      from: this.fromDate || null,
      to: this.toDate || null,
    });
  }

  close() {
    this.modalController.dismiss();
  }
}
